import React from "react";
import { cn } from "@/lib/utils";

interface LoadingSplashProps {
  className?: string;
  message?: string;
  variant?: "default" | "success" | "waiting";
}

export const LoadingSplash: React.FC<LoadingSplashProps> = ({
  className,
  message = "Processando sua pergunta...",
  variant = "default",
}) => {
  // Variante específica para "Respostas Enviadas!"
  if (variant === "success") {
    return (
      <div
        className={cn(
          "fixed inset-0 bg-background/90 backdrop-blur-md z-50",
          "flex flex-col items-center justify-center gap-6",
          className,
        )}
      >
        {/* Ícone de sucesso animado */}
        <div className="relative">
          <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center animate-pulse">
            <svg
              className="w-10 h-10 text-white animate-bounce"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          {/* Círculos orbitais */}
          <div className="absolute inset-0 w-20 h-20 border-2 border-green-400/30 rounded-full animate-ping" />
          <div className="absolute inset-0 w-20 h-20 border-2 border-green-300/20 rounded-full animate-ping" style={{ animationDelay: "0.5s" }} />
        </div>

        {/* Mensagem principal */}
        <div className="text-center space-y-3">
          <h3 className="text-xl font-semibold text-green-400 animate-pulse">
            Respostas Enviadas!
          </h3>
          <p className="text-sm text-gray-300">
            Suas respostas foram enviadas ao assistente.
          </p>
        </div>

        {/* Indicador de aguardo */}
        <div className="flex flex-col items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="flex space-x-1">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" />
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "0.1s" }} />
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }} />
            </div>
            <span className="text-xs text-gray-400 animate-pulse">
              Aguardando novas perguntas do assistente...
            </span>
          </div>
          <p className="text-xs text-gray-500 text-center max-w-xs">
            O painel reaparecerá automaticamente quando novas perguntas chegarem
          </p>
        </div>

        {/* Barra de progresso animada */}
        <div className="w-64 h-1 bg-gray-700 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full animate-pulse" 
               style={{ 
                 animation: "progress 2s ease-in-out infinite",
                 width: "100%"
               }} />
        </div>

        {/* Barra de progresso animada */}
        <div className="w-64 h-1 bg-gray-700 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full" 
            style={{ 
              animation: "progress 2s ease-in-out infinite",
              width: "100%"
            }} 
          />
        </div>

        {/* Estilo CSS inline para animação de progresso */}
        <style dangerouslySetInnerHTML={{
          __html: `
            @keyframes progress {
              0% { transform: translateX(-100%); }
              50% { transform: translateX(0%); }
              100% { transform: translateX(100%); }
            }
          `
        }} />
      </div>
    );
  }

  // Variante para aguardo
  if (variant === "waiting") {
    return (
      <div
        className={cn(
          "fixed inset-0 bg-background/90 backdrop-blur-md z-50",
          "flex flex-col items-center justify-center gap-6",
          className,
        )}
      >
        {/* Spinner melhorado */}
        <div className="relative">
          <div className="w-16 h-16 border-4 border-blue-500/20 rounded-full" />
          <div className="absolute inset-0 w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <div className="absolute inset-0 w-16 h-16 border-4 border-green-500/30 border-r-transparent rounded-full animate-spin" style={{ animationDirection: "reverse", animationDuration: "1.5s" }} />
        </div>

        {/* Mensagem */}
        <div className="text-center space-y-2">
          <p className="text-sm text-gray-300 animate-pulse">{message}</p>
          <div className="flex items-center justify-center gap-2">
            <div className="flex space-x-1">
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" />
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }} />
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "0.4s" }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Variante padrão (original melhorada)
  return (
    <div
      className={cn(
        "fixed inset-0 bg-background/80 backdrop-blur-sm z-50",
        "flex flex-col items-center justify-center gap-4",
        className,
      )}
    >
      <div className="relative">
        <div className="w-16 h-16 border-4 border-primary/20 rounded-full" />
        <div className="absolute inset-0 w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
      <p className="text-sm text-muted-foreground animate-pulse">{message}</p>
    </div>
  );
};
